import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {Advertisement} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/advertisement.types';
import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {GetBasketRecommendationsResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/recommendations/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useBasketRoute, useHistory} from '@aboutyou/router';
import {useTrackFeature} from '@aboutyou/tracking';
import {
  BasketFlyoutSuccessButtons,
  COLORS,
  ModalDialog,
  Z_INDEXES,
} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {AddToBasketSuccessSections} from '../../../features/AddToBasketSuccessSections/AddToBasketSuccessSections';
import {PromotionInvalidSection} from '../../../features/AddToBasketSuccessSections/PromotionInvalidSection';
import {useAuthenticationPrompt} from '../../../features/Authentication/AuthenticationProvider/useAuthenticationPrompt';
import {BasketUrgencyHint} from '../../../features/BasketUrgencyHint/BasketUrgencyHint';
import {getBasketUrgencyFromSections} from '../../../features/BasketUrgencyHint/utils/getBasketUrgencyFromSections';
import {getPromotionInvalidFromSections} from '../../../features/BasketUrgencyHint/utils/getPromotionInvalidSection';
import {useGoToCheckoutCallback} from '../../../features/Checkout/useGoToCheckoutCallback';
import {FreeDeliverySectionView} from '../../../features/FreeDeliverySectionView/FreeDeliverySectionView';
import {
  MiniAdpDialogOpenedStateKey,
  useMiniAdpDialogOpenedState,
} from '../../../features/MiniADPDialog/useMiniAdpDialogOpenedState';
import {useApiABTestSimilarItems} from '../../ArticleDetail/utils/useApiABTestSimilarItems';
import {BasketBannerView} from '../BasketBanner/BasketBannerView';
import {
  BasketFlyoutRecommendationView,
  StyledTextBanner,
} from '../Recommendations/BasketFlyoutRecommendationView.eager';
import {useBasketFlyoutOpenedState} from '../hooks/useBasketFlyoutOpenedState';

const BasketBannerViewForAds: React.FC<{
  advertisement?: Advertisement;
}> = ({advertisement}) => {
  useApiABTestSimilarItems();

  if (!advertisement) {
    return null;
  }

  return (
    <BasBannerContainer>
      <BasketBannerView
        containBanner
        type={{$case: 'ads', ads: {advertisement}}}
        promotionSource={TrackingPromotionCreative.BasAddToBasketFlyOut}
      />
    </BasBannerContainer>
  );
};

export const BasketFlyoutContentView: React.FC<
  GetBasketRecommendationsResponse & {
    freeDeliverySection?: FreeDeliverySection;
    statusSuccessSections: AddItemResponseStatus_Success_Section[];
    miniADPModalStateManagerKey?: MiniAdpDialogOpenedStateKey;
    basketFlyoutStateManagerKey?: string;
  }
> = ({
  advertisement,
  freeDeliverySection,
  recommendation,
  statusSuccessSections,
  miniADPModalStateManagerKey,
  basketFlyoutStateManagerKey,
}) => {
  const {hideFlyout: hideBasketFlyout} = useBasketFlyoutOpenedState(
    basketFlyoutStateManagerKey,
  );
  const {t} = useTranslation();

  const trackFeature = useTrackFeature();
  const {pushWithScrollTopAnchor} = useHistory();
  const {showUserAuthenticationPrompt} = useAuthenticationPrompt();
  const basketRoute = useBasketRoute();
  const goToCheckoutCallback = useGoToCheckoutCallback();
  const {open: openProductMiniADPDialog} = useMiniAdpDialogOpenedState(
    miniADPModalStateManagerKey,
  );
  const {isActive: isGuestCheckoutActive} = useIsFeatureActive(
    'enable_guest_checkout',
  );

  const onNavigateToBasket = () => {
    trackFeature({eventType: 'click', label: 'basketFlyout_toBasket'});
    pushWithScrollTopAnchor(basketRoute);
  };

  const onGoTocheckoutPressed = async () => {
    trackFeature({eventType: 'click', label: 'basketFlyout_toCheckout'});
    hideBasketFlyout({withNewHistoryEntry: true});

    // to be removed when openStateManager can handle two state changes in short time
    setTimeout(() => {
      showUserAuthenticationPrompt({
        context: 'basket-flyout-go-to-checkout',
        needsCheckoutSession: true,
        allowGuestLogin: isGuestCheckoutActive,
        onSuccess: goToCheckoutCallback,
        replaceHistory: true,
        forceRedirectToCheckoutOnSSOLoginSuccess: true,
      });
    });
  };

  const onClickAddToBasket = (productId: number | undefined) => {
    trackFeature({eventType: 'click', label: 'basketFlyout_A2B'});

    if (productId) {
      // we need to hide the flyout with a new history entry here in order to be able to navigate back to the flyout after closing the adp modal
      hideBasketFlyout({withNewHistoryEntry: true});

      // to be removed when openStateManager can handle two state changes in short time
      setTimeout(() => {
        openProductMiniADPDialog({
          productId,
          locationId: 'BasketFlyoutRecommendation',
        });
      }, 100);
    }
  };

  const basketUrgency = getBasketUrgencyFromSections(statusSuccessSections);

  const promotionInvalidSection = getPromotionInvalidFromSections(
    statusSuccessSections,
  );

  return (
    <>
      {promotionInvalidSection && (
        <PromotionInvalidSection promotionInvalid={promotionInvalidSection} />
      )}

      <StyledFreeDeliverySectionView
        freeDeliverySection={freeDeliverySection}
        ProgressMoreInfoLayoutComponent={StyledModalDialog}
      />
      <BasketUrgencyHintWrapper>
        <BasketUrgencyHint basketUrgency={basketUrgency} />
      </BasketUrgencyHintWrapper>
      <ButtonWrapper>
        <BasketFlyoutSuccessButtons
          onClickPrimaryButton={onNavigateToBasket}
          onClickSecondaryButton={onGoTocheckoutPressed}
          primaryButtonText={t('Go to basket')}
          secondaryButtonText={t('Go to checkout')}
        />
      </ButtonWrapper>
      <StyledAddToBasketSuccessSections
        statusSuccessSections={statusSuccessSections}
      />
      <BasketBannerViewForAds advertisement={advertisement} />
      <StyledBasketFlyoutRecommendationView
        onClickAddToBasket={onClickAddToBasket}
        recommendation={recommendation}
      />
    </>
  );
};

const BasketUrgencyHintWrapper = styled.div`
  margin-top: 16px;
`;

const ButtonWrapper = styled.div`
  padding: 16px 20px;
`;

const BasBannerContainer = styled.div`
  padding: 20px 20px 0 20px;
`;

const StyledFreeDeliverySectionView = styled(FreeDeliverySectionView)`
  border-bottom: 1px solid ${COLORS.Grey3};
  padding: 0.75rem 1rem;
`;

const StyledAddToBasketSuccessSections = styled(AddToBasketSuccessSections)`
  border-top: 1px solid ${COLORS.Grey3};
`;

const StyledModalDialog = styled(ModalDialog)`
  &&& {
    z-index: ${Z_INDEXES.MOV_BASKET_FLYOUT_INFO_MODAL};
  }
`;

const StyledBasketFlyoutRecommendationView = styled(
  BasketFlyoutRecommendationView,
)`
  padding: 20px 8px;

  ${StyledTextBanner} {
    padding: 0 12px;
  }
`;
